/**
 * The desktop title strip, Claude-desktop style. Rendered only inside the
 * Electron window (where `window.bimgo` exists), it replaces the native title
 * bar and menu entirely: a draggable strip with the menu and the sidebar toggle
 * on the **left** and the caption buttons — minimise, maximise/restore, close —
 * on the **right**, all driven over the bridge (`window.bimgo.menu.*` v3,
 * `window.bimgo.window.*` v8). No title text — the mark is the identity.
 *
 * The caption buttons used to be Electron's native `titleBarOverlay`: a corner
 * Windows painted in its own colour, hover and metrics. It never matched the
 * buttons beside it, and being native it sat above every DOM element — a banner
 * floated top-right disappeared under it. Now it is three more `.titlebar-btn`s
 * in the same flex row, so there is one strip, one style, and nothing the page
 * cannot draw over. Against a desktop older than v8 the overlay is still there
 * and `window.bimgo.window` is not, so no buttons are drawn — six for three
 * jobs would be worse than the seam.
 *
 * The menu has two groups, **View** and **Help**, each expanding in place.
 * `Help ▸ About` opens the technical detail: desktop version, the agent's
 * address, the installed plugin and the connected instances. That dialog is
 * where the agent's base URL lives now — it used to sit in the status bar, where
 * an ephemeral port that changes every launch was a moving number that told
 * nobody anything.
 *
 * The strip is a drag region (`-webkit-app-region: drag`); the interactive menu
 * opts back out with `no-drag`, or it could not be clicked.
 *
 * The shortcut hints below are honest, but this component does not implement
 * them: the accelerators live in the main process (`before-input-event`), so
 * they also work on the offline page, which is a `data:` document with none of
 * this code in it.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useLocale } from "../i18n/LocaleProvider";
import { sidebarStore } from "../lib/sidebarStore";
import { SIDEBAR_DRAWER_QUERY } from "../lib/sidebarView";
import { titlebarSlot } from "../lib/titlebarSlot";
import { useDiagnostics } from "../hooks/useDiagnostics";
import { AboutDialog } from "./AboutDialog";
import { DiagnosticsFeedback } from "./DiagnosticsFeedback";

function MenuListIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 6h13" />
      <path d="M8 12h13" />
      <path d="M8 18h13" />
      <path d="M3 6h.01" />
      <path d="M3 12h.01" />
      <path d="M3 18h.01" />
    </svg>
  );
}
/** lucide `PanelLeft` — the glyph the old web's sidebar toggle uses. */
function PanelLeftIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M9 3v18" />
    </svg>
  );
}
function ReloadIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v6h-6" />
    </svg>
  );
}
function DevToolsIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m8 8-4 4 4 4" />
      <path d="m16 8 4 4-4 4" />
      <path d="m13 5-2 14" />
    </svg>
  );
}

function AboutIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </svg>
  );
}

/** The three caption glyphs, at the 10px hairline weight Windows draws its
 *  own at, so they read as the caption buttons they replace. */
function MinimizeIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1" aria-hidden="true">
      <path d="M0 5h10" />
    </svg>
  );
}
function MaximizeIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1" aria-hidden="true">
      <rect x="0.5" y="0.5" width="9" height="9" />
    </svg>
  );
}
/** Two offset squares — the Windows "restore down" glyph. */
function RestoreIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1" aria-hidden="true">
      <rect x="0.5" y="2.5" width="7" height="7" />
      <path d="M2.5 2.5v-2h7v7h-2" />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1" aria-hidden="true">
      <path d="M0 0l10 10M10 0L0 10" />
    </svg>
  );
}

/** lucide `Archive` — the zip. */
function PackageIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect width="20" height="5" x="2" y="3" rx="1" />
      <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
      <path d="M10 12h4" />
    </svg>
  );
}

export function DesktopTitlebar() {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  // Help → Package logs. Present only when this desktop's bridge has the
  // surface (v6); an older desktop gets no item, not a dead one.
  const diagnostics = useDiagnostics();
  const collapsed = useSyncExternalStore(sidebarStore.subscribe, sidebarStore.collapsed);
  /**
   * The toggle drives the app shell's rail, so it is drawn only where that rail
   * is (owner, 2026-08-25: on the login page it would be a button for nothing).
   *
   * This asked whether there was a **session**, which was the same question
   * while login was the only screen without a shell. The organization gate has
   * a session and no shell, so the button survived onto a page with no rail to
   * collapse. Asking the shell whether it is mounted is the question that was
   * always meant.
   */
  const shellMounted = useSyncExternalStore(sidebarStore.subscribe, sidebarStore.shellMounted);
  /** Which group is expanded, or none. One at a time — this is a small strip. */
  const [section, setSection] = useState<"view" | "help" | null>(null);
  const [about, setAbout] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // The caption buttons are feature-detected once: a v7 desktop has no
  // `window` surface and is still painting its native overlay in this corner.
  const windowBridge = window.bimgo?.window ?? null;
  const [maximized, setMaximized] = useState(false);
  useEffect(() => {
    if (!windowBridge) return;
    let cancelled = false;
    void windowBridge.isMaximized().then((value) => {
      if (!cancelled) setMaximized(value);
    });
    // Keyboard and taskbar maximize too, so the glyph follows the window, not
    // the button.
    const unsubscribe = windowBridge.onStateChange(({ maximized: next }) => setMaximized(next));
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [windowBridge]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      // Only the menu. The dialog owns its own Escape, and closing both from
      // here would shut the dialog the moment it opened.
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  /** Closing the menu resets it, so it does not reopen mid-expanded. */
  const closeMenu = () => {
    setOpen(false);
    setSection(null);
  };

  const reload = () => {
    window.bimgo?.menu?.reload();
    closeMenu();
  };
  const toggleDevTools = () => {
    window.bimgo?.menu?.toggleDevTools();
    closeMenu();
  };
  const openAbout = () => {
    closeMenu();
    setAbout(true);
  };
  const packageLogs = () => {
    closeMenu();
    void diagnostics.packageLogs();
  };

  return (
    <div className="titlebar">
      {/* The menu and the sidebar toggle, as one group. While the sidebar is
          open (wide windows) the group is exactly the sidebar's width, so the
          instance chips after it start at the sidebar's right edge — above the
          pane they belong to, not wherever the buttons happen to end. */}
      <div className={`titlebar-lead${shellMounted && !collapsed ? " is-sidebar-width" : ""}`}>
        {/* No mark here: the sidebar's lockup sits directly below it, and two
            logos a few pixels apart is one too many. */}
        <div className="titlebar-menu" ref={ref}>
          <button
            type="button"
            className="titlebar-btn"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={t("menu.label")}
            title={t("menu.label")}
            onClick={() => setOpen((v) => !v)}
          >
            <MenuListIcon />
          </button>
          {open ? (
            <div className="titlebar-dropdown" role="menu">
              {/* Two groups, each expanding in place. A hover flyout is the other
                  way to nest a menu and it is the wrong one here: this dropdown
                  hangs off a single button in a 32px strip, where a submenu that
                  opens sideways has nowhere to go and no keyboard story. Clicking
                  a group is unambiguous with a mouse and with a keyboard. */}
              <MenuGroup
                label={t("menu.view")}
                expanded={section === "view"}
                onToggle={() => setSection((s) => (s === "view" ? null : "view"))}
              >
                <button type="button" role="menuitem" className="titlebar-item" onClick={reload}>
                  <span className="titlebar-item-icon">
                    <ReloadIcon />
                  </span>
                  <span className="titlebar-item-label">{t("menu.reload")}</span>
                  <kbd className="titlebar-item-kbd">Ctrl+R</kbd>
                </button>
                <button type="button" role="menuitem" className="titlebar-item" onClick={toggleDevTools}>
                  <span className="titlebar-item-icon">
                    <DevToolsIcon />
                  </span>
                  <span className="titlebar-item-label">{t("menu.devtools")}</span>
                  <kbd className="titlebar-item-kbd">Ctrl+Shift+I</kbd>
                </button>
              </MenuGroup>

              <div className="titlebar-sep" />

              <MenuGroup
                label={t("menu.help")}
                expanded={section === "help"}
                onToggle={() => setSection((s) => (s === "help" ? null : "help"))}
              >
                <button type="button" role="menuitem" className="titlebar-item" onClick={openAbout}>
                  <span className="titlebar-item-icon">
                    <AboutIcon />
                  </span>
                  <span className="titlebar-item-label">{t("menu.about")}</span>
                </button>
                {diagnostics.available ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="titlebar-item"
                    disabled={diagnostics.busy}
                    onClick={packageLogs}
                  >
                    <span className="titlebar-item-icon">
                      <PackageIcon />
                    </span>
                    <span className="titlebar-item-label">{t("menu.packageLogs")}</span>
                  </button>
                ) : null}
              </MenuGroup>
            </div>
          ) : null}
        </div>

        {/* The sidebar toggle, beside the app menu — the old web's `PanelLeft`
            affordance, and the **only** toggle: the titlebar is always there, so
            the shell carries no opener of its own. One meaning per viewport
            (`toggleFor`): at drawer widths it opens/closes the drawer, at rail
            widths it flips the persisted preference. The breakpoint matches the
            CSS's md cutoff. */}
        {shellMounted ? (
          <button
            type="button"
            className="titlebar-btn"
            aria-pressed={collapsed}
            aria-label={t(collapsed ? "shell.sidebarExpand" : "shell.sidebarCollapse")}
            title={t(collapsed ? "shell.sidebarExpand" : "shell.sidebarCollapse")}
            onClick={() => sidebarStore.toggleFor(window.matchMedia(SIDEBAR_DRAWER_QUERY).matches)}
          >
            <PanelLeftIcon />
          </button>
        ) : null}
      </div>

      {/* Where the shell puts the connected-instance chips — always, so the
          connected Revit/AutoCAD is in sight on every route, starting at the
          sidebar's edge while it is open (`.titlebar-lead`). The shell fills it
          through a portal (`titlebarSlot`), because the selection and
          its lock are the shell's state, not this strip's. */}
      {shellMounted ? <div className="titlebar-slot" ref={titlebarSlot.set} /> : null}

      {/* The caption buttons, pushed to the right edge of the same flex row.
          Same `.titlebar-btn` as the menu and the toggle — that sameness is the
          point. Close alone gets the red hover every Windows user expects; a
          close button that hovers like a menu item is one people miss. */}
      {windowBridge ? (
        <div className="titlebar-controls">
          <button
            type="button"
            className="titlebar-btn titlebar-caption"
            aria-label={t("window.minimize")}
            title={t("window.minimize")}
            onClick={() => windowBridge.minimize()}
          >
            <MinimizeIcon />
          </button>
          <button
            type="button"
            className="titlebar-btn titlebar-caption"
            aria-label={t(maximized ? "window.restore" : "window.maximize")}
            title={t(maximized ? "window.restore" : "window.maximize")}
            onClick={() => windowBridge.toggleMaximize()}
          >
            {maximized ? <RestoreIcon /> : <MaximizeIcon />}
          </button>
          <button
            type="button"
            className="titlebar-btn titlebar-caption is-close"
            aria-label={t("window.close")}
            title={t("window.close")}
            onClick={() => windowBridge.close()}
          >
            <CloseIcon />
          </button>
        </div>
      ) : null}

      <AboutDialog open={about} onClose={() => setAbout(false)} />
      <DiagnosticsFeedback diagnostics={diagnostics} />
    </div>
  );
}

/** One expandable group in the dropdown. */
function MenuGroup({
  label,
  expanded,
  onToggle,
  children,
}: {
  label: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="titlebar-group">
      <button
        type="button"
        className="titlebar-item titlebar-group-header"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <span className="titlebar-item-label">{label}</span>
        <span className="titlebar-group-caret" aria-hidden="true">
          {expanded ? "▾" : "▸"}
        </span>
      </button>
      {expanded ? <div className="titlebar-group-items">{children}</div> : null}
    </div>
  );
}
